"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function QuoteCta() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Guildford",
    to: "/AreasWeCoverPage"
  }, {
    name: "Camberley",
    to: "/AreasWeCoverPage"
  }];
  return <section id="finalquotecta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}>
          <Card className="bg-secondary text-secondary-foreground border-border shadow-xl overflow-hidden rounded-2xl p-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Main CTA Column */}
              <div className="lg:col-span-7 p-8 sm:p-10 md:p-12 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-md bg-accent/15 px-3 py-1 text-xs font-semibold tracking-wide text-primary-foreground mb-6">
                    <ShieldCheck className="size-4 text-primary" />
                    <span>NASC &amp; CHAS accredited</span>
                  </div>

                  <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-secondary-foreground mb-4">
                    Request a scaffolding quote
                  </h2>

                  <p className="text-base sm:text-lg text-secondary-foreground/85 leading-relaxed mb-8 max-w-xl">
                    Get in touch with Pete Wood and the operational team to discuss project requirements, site specifications, and clear scheduling.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-3 rounded-lg shadow-md transition-colors">
                    <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                      <span>Request your quote</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Direct Contact Column */}
              <div className="lg:col-span-5 bg-secondary-foreground/5 border-t lg:border-t-0 lg:border-l border-border/20 p-8 sm:p-10 md:p-12 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-secondary-foreground mb-6">
                    Direct communication
                  </h3>

                  <div className="space-y-4">
                    <a href="tel:01344929900" className="group flex items-start gap-4 p-4 rounded-xl bg-secondary hover:bg-secondary/80 text-secondary-foreground border border-border/20 transition-colors">
                      <div className="rounded-lg bg-primary/20 p-2.5 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Phone className="size-5" />
                      </div>
                      <div>
                        <span className="block text-xs font-medium text-secondary-foreground/70 mb-0.5">
                          Call directly
                        </span>
                        <span className="text-base font-semibold text-secondary-foreground group-hover:text-primary transition-colors">
                          01344 929900
                        </span>
                      </div>
                    </a>

                    <a href="mailto:quotes@bmfscaffolding.co.uk" className="group flex items-start gap-4 p-4 rounded-xl bg-secondary hover:bg-secondary/80 text-secondary-foreground border border-border/20 transition-colors">
                      <div className="rounded-lg bg-primary/20 p-2.5 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Mail className="size-5" />
                      </div>
                      <div>
                        <span className="block text-xs font-medium text-secondary-foreground/70 mb-0.5">
                          Email specifications
                        </span>
                        <span className="text-sm sm:text-base font-semibold text-secondary-foreground group-hover:text-primary transition-colors break-all">
                          quotes@bmfscaffolding.co.uk
                        </span>
                      </div>
                    </a>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border/20">
                  <div className="flex items-center gap-2 text-xs font-medium text-secondary-foreground/70 mb-2">
                    <MapPin className="size-3.5 text-primary" />
                    <span>Nearby areas</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                    {nearbyAreas.map((area, i) => <div key={area.name} data-index={i} className="flex items-center gap-4">
                        <Link to={area.to} className="text-secondary-foreground/90 hover:text-primary underline underline-offset-4 transition-colors">
                          {area.name}
                        </Link>
                        {i < nearbyAreas.length - 1 && <span className="text-border text-xs" aria-hidden="true">•</span>}
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>;
}